import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, contractsApi } from '../lib/api';
import api from '../lib/api';
import { formatDate, formatDateTime, STATUS_LABELS, getStatusColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
    FileText,
    CheckCircle,
    Warning,
    Clock,
    ArrowRight,
    Pulse,
    Lightning,
    Users,
    Buildings,
    MapPin,
    ChartBar,
    Gauge,
    TrendUp,
    BatteryCharging,
    Timer,
    CaretUp,
    CaretDown,
    Minus,
    Target,
    Fire
} from '@phosphor-icons/react';

// Simple mini chart component
const MiniBarChart = ({ data, maxValue, color = 'bg-primary' }) => {
    const max = maxValue || Math.max(...Object.values(data), 1);
    return (
        <div className="flex items-end gap-1 h-16">
            {Object.entries(data).slice(-6).map(([key, value], idx) => (
                <div key={key} className="flex flex-col items-center flex-1">
                    <div 
                        className={`w-full ${color} rounded-t transition-all duration-500`}
                        style={{ height: `${(value / max) * 100}%`, minHeight: value > 0 ? '4px' : '0' }}
                    />
                    <span className="text-[9px] text-muted-foreground mt-1 truncate w-full text-center">
                        {key.slice(-2)}
                    </span>
                </div>
            ))}
        </div>
    );
};

// Donut progress indicator
const DonutProgress = ({ value, size = 80, strokeWidth = 8, color = '#22c55e' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;
    
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg className="transform -rotate-90" width={size} height={size}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                    className="text-gray-200"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className="transition-all duration-700"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold">{Math.round(value)}%</span>
            </div>
        </div>
    );
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue }) => (
    <Card className="card-hover">
        <CardContent className="p-4">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                        {title}
                    </p>
                    <p className="text-2xl font-heading font-bold">{value}</p>
                    {subtitle && (
                        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
                    )}
                    {trend && (
                        <div className={`flex items-center gap-1 mt-2 text-xs ${
                            trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                            {trend === 'up' ? <CaretUp weight="bold" className="h-3 w-3" /> :
                             trend === 'down' ? <CaretDown weight="bold" className="h-3 w-3" /> :
                             <Minus weight="bold" className="h-3 w-3" />}
                            <span>{trendValue}</span>
                        </div>
                    )}
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                    <Icon weight="fill" className="w-5 h-5 text-white" />
                </div>
            </div>
        </CardContent>
    </Card>
);

export default function DashboardPage() {
    const navigate = useNavigate();
    const { isStoreAgent } = useAuth();
    const [overview, setOverview] = useState(null);
    const [kpi, setKpi] = useState(null);
    const [advancedKpi, setAdvancedKpi] = useState(null);
    const [recentContracts, setRecentContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    // Redirect store_agent to /store
    useEffect(() => {
        if (isStoreAgent) {
            navigate('/store', { replace: true });
        }
    }, [isStoreAgent, navigate]);

    useEffect(() => {
        if (!isStoreAgent) {
            loadData();
        }
    }, [isStoreAgent]);

    const loadData = async () => {
        try {
            const [overviewRes, kpiRes, contractsRes, advancedRes] = await Promise.all([
                dashboardApi.getOverview(),
                dashboardApi.getKpi(),
                contractsApi.getAll({ limit: 5 }),
                api.get('/dashboard/kpi-advanced')
            ]);
            setOverview(overviewRes.data);
            setKpi(kpiRes.data);
            setRecentContracts(contractsRes.data.slice(0, 5));
            setAdvancedKpi(advancedRes.data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const stats = [
        {
            title: 'Commesse Totali',
            value: advancedKpi?.master_stats?.total_contracts || overview?.total_contracts || 0,
            icon: FileText,
            color: 'bg-primary'
        },
        {
            title: 'In Corso',
            value: overview?.contracts_in_corso || 0,
            icon: Clock,
            color: 'bg-blue-500'
        },
        {
            title: 'Completate',
            value: overview?.contracts_completati || 0,
            icon: CheckCircle,
            color: 'bg-green-500'
        },
        {
            title: 'In Ritardo',
            value: advancedKpi?.performance?.overdue_contracts ?? overview?.overdue_nodes ?? 0,
            icon: Warning,
            color: 'bg-amber-500'
        }
    ];

    const plantMetrics = advancedKpi?.plant_metrics || {};
    const workflowMetrics = advancedKpi?.workflow_metrics || {};
    const geographic = advancedKpi?.geographic || {};
    const performance = advancedKpi?.performance || {};
    const timeline = advancedKpi?.timeline?.contracts_by_month || {};

    return (
        <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
            {/* Header with Tabs */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-xl font-semibold">Dashboard</h2>
                    <p className="text-sm text-muted-foreground">
                        Panoramica operativa CRM Fotovoltaico
                    </p>
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="overview" className="text-xs">
                            <ChartBar className="h-4 w-4 mr-1" />
                            Overview
                        </TabsTrigger>
                        <TabsTrigger value="technical" className="text-xs">
                            <Lightning className="h-4 w-4 mr-1" />
                            Impianti
                        </TabsTrigger>
                        <TabsTrigger value="performance" className="text-xs">
                            <Gauge className="h-4 w-4 mr-1" />
                            Performance
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* Main Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat) => {
                    const Icon = stat.icon;
                    return (
                        <Card key={stat.title} className="card-hover" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
                            <CardContent className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                                            {stat.title}
                                        </p>
                                        <p className="text-3xl font-heading font-bold">{stat.value}</p>
                                    </div>
                                    <div className={`w-12 h-12 rounded-md flex items-center justify-center ${stat.color}`}>
                                        <Icon weight="fill" className="w-6 h-6 text-white" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {activeTab === 'overview' && (
                <>
                    {/* KPI Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <KPICard
                            title="Potenza Totale"
                            value={`${plantMetrics.total_power_kw?.toFixed(1) || 0} kW`}
                            subtitle={`${plantMetrics.total_power_mw?.toFixed(2) || 0} MW`}
                            icon={Lightning}
                            color="bg-purple-500"
                        />
                        <KPICard
                            title="Clienti"
                            value={advancedKpi?.master_stats?.total_customers || 0}
                            subtitle="Anagrafica"
                            icon={Users}
                            color="bg-green-500"
                        />
                        <KPICard
                            title="Siti"
                            value={advancedKpi?.master_stats?.total_sites || 0}
                            subtitle="Immobili"
                            icon={Buildings}
                            color="bg-amber-500"
                        />
                        <KPICard
                            title="Con Storage"
                            value={plantMetrics.plants_with_storage || 0}
                            subtitle="Batterie"
                            icon={BatteryCharging}
                            color="bg-cyan-500"
                        />
                        <KPICard
                            title="Avanzamento Medio"
                            value={`${workflowMetrics.avg_progress_percentage || 0}%`}
                            subtitle={`${workflowMetrics.total_completed_steps || 0}/${workflowMetrics.total_steps || 0} step`}
                            icon={Target}
                            color="bg-indigo-500"
                        />
                        <KPICard
                            title="Tempo Medio"
                            value={`${performance.avg_completion_days || 0}gg`}
                            subtitle="Completamento"
                            icon={Timer}
                            color="bg-rose-500"
                        />
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Recent Contracts */}
                        <Card className="lg:col-span-2">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="font-heading text-lg">Contratti Recenti</CardTitle>
                                    <Link
                                        to="/contratti"
                                        data-testid="view-all-contracts-link"
                                        className="text-sm text-primary hover:underline flex items-center gap-1"
                                    >
                                        Vedi tutti <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y divide-border">
                                    {recentContracts.length === 0 ? (
                                        <div className="p-6 text-center text-muted-foreground">
                                            Nessun contratto presente
                                        </div>
                                    ) : (
                                        recentContracts.map((contract) => (
                                            <Link
                                                key={contract.id}
                                                to={`/contratti/${contract.id}`}
                                                data-testid={`contract-row-${contract.id}`}
                                                className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-sm">
                                                            {contract.numero_contratto}
                                                        </span>
                                                        <Badge variant="outline" className="text-xs">
                                                            {contract.product_name || contract.codice_prodotto || '-'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {contract.customer_name || contract.indirizzo_impianto}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <Badge className={getStatusColor(contract.status)}>
                                                        {STATUS_LABELS[contract.status] || contract.status}
                                                    </Badge>
                                                    <div className="w-24">
                                                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                                            <span>Progresso</span>
                                                            <span>{Math.round(contract.progress || 0)}%</span>
                                                        </div>
                                                        <Progress value={contract.progress || 0} className="h-1.5" />
                                                    </div>
                                                </div>
                                            </Link>
                                        ))
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Activity + Trend */}
                        <div className="space-y-6">
                            {/* Timeline Chart */}
                            {Object.keys(timeline).length > 0 && (
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm flex items-center gap-2">
                                            <TrendUp weight="fill" className="h-4 w-4 text-primary" />
                                            Trend Commesse
                                        </CardTitle>
                                        <CardDescription className="text-xs">Ultimi 6 mesi</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <MiniBarChart data={timeline} color="bg-primary" />
                                    </CardContent>
                                </Card>
                            )}

                            {/* Activity Feed */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="font-heading text-sm flex items-center gap-2">
                                        <Pulse weight="fill" className="w-4 h-4 text-accent" />
                                        Attività Recente
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="divide-y divide-border max-h-64 overflow-y-auto">
                                        {overview?.recent_activity?.length === 0 ? (
                                            <div className="p-4 text-center text-muted-foreground text-sm">
                                                Nessuna attività
                                            </div>
                                        ) : (
                                            overview?.recent_activity?.slice(0, 5).map((activity) => (
                                                <div key={activity.id} className="p-3">
                                                    <p className="text-xs">{activity.description}</p>
                                                    <div className="flex items-center justify-between mt-1">
                                                        <span className="text-[10px] text-muted-foreground">
                                                            {activity.user_name}
                                                        </span>
                                                        <span className="text-[10px] text-muted-foreground">
                                                            {formatDateTime(activity.created_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'technical' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Power Distribution */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Lightning weight="fill" className="h-4 w-4 text-purple-500" />
                                Distribuzione Potenze
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {Object.entries(plantMetrics.power_distribution || {}).map(([range, count]) => (
                                    <div key={range} className="flex items-center justify-between">
                                        <span className="text-sm">{range}</span>
                                        <div className="flex items-center gap-2">
                                            <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-purple-500 rounded-full"
                                                    style={{ 
                                                        width: `${(count / (advancedKpi?.master_stats?.total_plants || 1)) * 100}%` 
                                                    }}
                                                />
                                            </div>
                                            <span className="text-sm font-medium w-8 text-right">{count}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Geographic Distribution */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <MapPin weight="fill" className="h-4 w-4 text-amber-500" />
                                Per Provincia
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {Object.entries(geographic.by_province || {}).slice(0, 6).map(([province, count]) => (
                                    <div key={province} className="flex items-center justify-between">
                                        <span className="text-sm font-medium">{province}</span>
                                        <Badge variant="outline">{count}</Badge>
                                    </div>
                                ))}
                                {Object.keys(geographic.by_province || {}).length === 0 && (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        Nessun dato disponibile
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Workflow Progress */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Target weight="fill" className="h-4 w-4 text-indigo-500" />
                                Avanzamento Workflow
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="flex items-center justify-center py-4">
                            <div className="text-center">
                                <DonutProgress 
                                    value={workflowMetrics.avg_progress_percentage || 0}
                                    color="#6366f1"
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    Media avanzamento
                                </p>
                                <p className="text-sm font-medium mt-1">
                                    {workflowMetrics.total_completed_steps || 0} / {workflowMetrics.total_steps || 0} step
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Customer Types */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Users weight="fill" className="h-4 w-4 text-green-500" />
                                Tipologia Clienti
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {Object.entries(advancedKpi?.customer_metrics?.type_distribution || {}).map(([type, count]) => (
                                    <div key={type} className="flex items-center justify-between">
                                        <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                                        <Badge variant="outline">{count}</Badge>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Workflow States */}
                    <Card className="md:col-span-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Gauge weight="fill" className="h-4 w-4 text-cyan-500" />
                                Stati Workflow
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4 flex-wrap">
                                {Object.entries(workflowMetrics.state_distribution || {}).map(([state, count]) => {
                                    const stateColors = {
                                        'in_corso': 'bg-blue-100 text-blue-800',
                                        'completato': 'bg-green-100 text-green-800',
                                        'sospeso': 'bg-amber-100 text-amber-800',
                                        'annullato': 'bg-red-100 text-red-800',
                                    };
                                    return (
                                        <div key={state} className={`px-4 py-3 rounded-lg ${stateColors[state] || 'bg-gray-100 text-gray-800'}`}>
                                            <p className="text-2xl font-bold">{count}</p>
                                            <p className="text-xs capitalize">{state.replace('_', ' ')}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'performance' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Step Performance */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Timer weight="fill" className="h-4 w-4 text-indigo-500" />
                                Performance Step
                            </CardTitle>
                            <CardDescription className="text-xs">Tempo medio di completamento</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {(performance.step_performance || []).slice(0, 8).map((step, idx) => (
                                    <div key={step.step_name} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className="text-xs text-muted-foreground w-4">{idx + 1}</span>
                                            <span className="text-sm truncate">{step.step_name}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-muted-foreground">
                                                {step.completions}x
                                            </span>
                                            <Badge variant={step.avg_hours < 24 ? 'default' : step.avg_hours < 72 ? 'secondary' : 'destructive'}>
                                                {step.avg_hours < 24 
                                                    ? `${step.avg_hours.toFixed(0)}h`
                                                    : `${(step.avg_hours / 24).toFixed(1)}gg`
                                                }
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                                {(performance.step_performance || []).length === 0 && (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        Nessun dato disponibile
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Longest Waiting */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Fire weight="fill" className="h-4 w-4 text-red-500" />
                                In Attesa da Più Tempo
                            </CardTitle>
                            <CardDescription className="text-xs">Step che richiedono attenzione</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {(performance.longest_waiting || []).map((item) => (
                                    <Link 
                                        key={`waiting-${item.contract_id}-${item.step_name}`}
                                        to={`/contratti/${item.contract_id}`}
                                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{item.step_name}</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {item.contract_id?.slice(0, 8)}...
                                            </p>
                                        </div>
                                        <Badge variant={item.days_waiting > 14 ? 'destructive' : item.days_waiting > 7 ? 'secondary' : 'outline'}>
                                            {item.days_waiting} giorni
                                        </Badge>
                                    </Link>
                                ))}
                                {(performance.longest_waiting || []).length === 0 && (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        Nessuno step in ritardo
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Bottlenecks */}
                    {kpi?.bottlenecks && kpi.bottlenecks.length > 0 && (
                        <Card className="md:col-span-2">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-heading text-sm flex items-center gap-2">
                                    <Warning weight="fill" className="w-4 h-4 text-amber-500" />
                                    Colli di Bottiglia
                                </CardTitle>
                                <CardDescription className="text-xs">Step con più pratiche ferme</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                    {kpi.bottlenecks.map((item) => (
                                        <div 
                                            key={`bottleneck-${item.name || item._id?.name}`}
                                            className="p-4 bg-amber-50 border border-amber-200 rounded-lg"
                                        >
                                            <p className="font-medium text-sm truncate">{item.name || item._id?.name}</p>
                                            <p className="text-2xl font-heading font-bold text-amber-700">
                                                {item.count}
                                            </p>
                                            <p className="text-xs text-amber-600">pratiche ferme</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* SLA Summary */}
                    <Card className="md:col-span-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Riepilogo Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center p-4 bg-green-50 rounded-lg">
                                    <p className="text-3xl font-bold text-green-700">{overview?.contracts_completati || 0}</p>
                                    <p className="text-xs text-green-600">Completati</p>
                                </div>
                                <div className="text-center p-4 bg-blue-50 rounded-lg">
                                    <p className="text-3xl font-bold text-blue-700">{overview?.contracts_in_corso || 0}</p>
                                    <p className="text-xs text-blue-600">In Corso</p>
                                </div>
                                <div className="text-center p-4 bg-amber-50 rounded-lg">
                                    <p className="text-3xl font-bold text-amber-700">{performance.overdue_contracts || 0}</p>
                                    <p className="text-xs text-amber-600">In Ritardo</p>
                                </div>
                                <div className="text-center p-4 bg-purple-50 rounded-lg">
                                    <p className="text-3xl font-bold text-purple-700">{performance.avg_completion_days || 0}</p>
                                    <p className="text-xs text-purple-600">Giorni Medi</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
